import React, { useState } from 'react';
import styles from './QRCodeDisplay.module.css';

interface QRCodeDisplayProps {
  qrCodeSvgUrl?: string;
  qrCodePngUrl?: string;
  onboardUrl?: string;
  venueName?: string;
  locationLabel?: string;
  type: 'venue' | 'location';
  onPrint?: () => void;
  buttonStyle?: 'inline' | 'venue';
}

// Button component for toggling QR code visibility
export const QRCodeButton: React.FC<QRCodeDisplayProps> = ({
  buttonStyle = 'inline'
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <button 
      onClick={() => setIsExpanded(!isExpanded)}
      className={buttonStyle === 'venue' ? styles.venueToggleButton : styles.inlineToggleButton}
    >
      {isExpanded ? 'Hide QR Code' : 'Show QR Code'}
    </button>
  );
};

// Content component for displaying QR code
export const QRCodeContent: React.FC<QRCodeDisplayProps> = ({
  qrCodeSvgUrl,
  qrCodePngUrl,
  onboardUrl,
  venueName,
  locationLabel,
  type,
  onPrint
}) => {
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState<'svg' | 'png'>('svg');

  const handlePrint = () => {
    setShowPrintDialog(true);
  };

  const handlePrintConfirm = () => {
    const qrCodeUrl = selectedFormat === 'svg' ? qrCodeSvgUrl : qrCodePngUrl;
    if (!qrCodeUrl) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title>Print QR Code</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              text-align: center; 
              padding: 20px;
            }
            .qr-code { 
              max-width: 300px; 
              height: auto; 
              margin: 20px 0;
            }
            .qr-title { 
              font-size: 18px; 
              font-weight: bold; 
              margin: 10px 0;
            }
            .qr-type { 
              color: #666; 
              margin: 5px 0;
            }
            .qr-url { 
              font-size: 12px; 
              color: #888; 
              word-break: break-all;
              margin: 10px 0;
            }
            @media print {
              body { margin: 0; padding: 10px; }
            }
          </style>
        </head>
        <body>
          <div class="qr-title">${type === 'venue' ? venueName : `${venueName} - ${locationLabel}`}</div>
          <div class="qr-type">${type === 'venue' ? 'Venue Check-in' : 'Location Check-in'}</div>
          <img src="${qrCodeUrl}" alt="QR Code" class="qr-code" />
          ${onboardUrl ? `<div class="qr-url">${onboardUrl}</div>` : ''}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
    setShowPrintDialog(false);
  };

  if (!qrCodeSvgUrl && !qrCodePngUrl) {
    return (
      <div className={styles.noQRCode}>
        <p>QR code not available</p>
      </div>
    );
  }

  return (
    <>
      <div className={styles.qrCodeSlideDown}>
        <div className={styles.qrCodeContent}>
          <div className={styles.qrCodeImage}>
            <img 
              src={qrCodeSvgUrl || qrCodePngUrl} 
              alt="QR Code" 
              className={styles.qrCode}
            />
          </div>
          
          <div className={styles.qrCodeInfo}>
            <div className={styles.qrCodeTitle}>
              {type === 'venue' ? venueName : `${venueName} - ${locationLabel}`}
            </div>
            <div className={styles.qrCodeType}>
              {type === 'venue' ? 'Venue Check-in' : 'Location Check-in'}
            </div>
            {onboardUrl && (
              <div className={styles.qrCodeUrl}>
                <strong>URL:</strong> {onboardUrl}
              </div>
            )}
          </div>
          
          <div className={styles.qrCodeActions}>
            <button 
              onClick={handlePrint}
              className={styles.printButton}
            >
              Print QR Code
            </button>
          </div>
        </div>
      </div>

      {showPrintDialog && (
        <div className={styles.printDialog}>
          <div className={styles.printDialogContent}>
            <h4>Print QR Code</h4>
            <div className={styles.formatSelection}>
              <label>
                <input
                  type="radio"
                  value="svg"
                  checked={selectedFormat === 'svg'}
                  onChange={(e) => setSelectedFormat(e.target.value as 'svg' | 'png')}
                />
                SVG (Vector - Better for printing)
              </label>
              <label>
                <input
                  type="radio"
                  value="png"
                  checked={selectedFormat === 'png'}
                  onChange={(e) => setSelectedFormat(e.target.value as 'svg' | 'png')}
                />
                PNG (Raster - Universal compatibility)
              </label>
            </div>
            <div className={styles.printDialogActions}>
              <button 
                onClick={() => setShowPrintDialog(false)}
                className={styles.cancelButton}
              >
                Cancel
              </button>
              <button 
                onClick={handlePrintConfirm}
                className={styles.confirmButton}
              >
                Print
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Main component that combines both (for backward compatibility)
const QRCodeDisplay: React.FC<QRCodeDisplayProps> = (props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!props.qrCodeSvgUrl && !props.qrCodePngUrl) {
    return (
      <div className={styles.noQRCode}>
        <p>QR code not available</p>
      </div>
    );
  }

  return (
    <>
      <button 
        onClick={() => setIsExpanded(!isExpanded)}
        className={props.buttonStyle === 'venue' ? styles.venueToggleButton : styles.inlineToggleButton}
      >
        {isExpanded ? 'Hide QR Code' : 'Show QR Code'}
      </button>
      
      {isExpanded && <QRCodeContent {...props} />}
    </>
  );
};

export default QRCodeDisplay;